// SPA route-change announcement for the authenticated app shell.
// react-router-dom has no built-in afterNavigate, and no route in this
// tree sets document.title (index.html leaves a static
// "BitácoraPro — Dashboard", a separate, larger gap — WCAG SC
// 2.4.2 — out of scope here). Without this, a screen-reader user gets no
// signal that navigating the sidebar changed the view at all, since
// react-router swaps <Outlet /> content in place with no document
// reload.
//
// Generalizes the pattern DevRoutes.tsx's DevNavAnnouncer already used
// for /dev/nav (accessibility-expert, remaining-work.md §4.10) — same
// mount-skip / debounce shape, but keyed off the same resolved nav list
// the Sidebar renders (lib/nav's useVisibleNavItems) instead of the
// dev-nav role list, since this is the main authenticated tree, not a
// component gallery. Reading the resolved list rather than the raw
// NAV_ITEMS is what makes "/proyectos" announce as the recipe's own
// label and "/observaciones" announce as "Observaciones" on facility.
//
// Deliberately does NOT attempt a per-entity title (e.g. a specific
// tienda's name on /tiendas/:id) — that needs each detail route to
// supply its own label, which is a larger, route-by-route follow-up.
// The project subtree (panel postventa v2: /proyectos/:id, its
// observations, their detail and the archive) is named by pattern below
// because the sidebar's prefix match would otherwise announce every one
// of those screens as «Proyectos». Falls back to the raw pathname for
// any route nothing names, which is still strictly better than silence.

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { navItemBasePath, useVisibleNavItems, type VisibleNavItem } from "../lib/nav";

// Most specific first: a pattern is a prefix of the ones below it.
const PROJECT_SUBTREE_TITLES: readonly { pattern: RegExp; title: string }[] = [
  { pattern: /^\/proyectos\/[^/]+\/observaciones\/[^/]+$/, title: "Observaciones — detalle" },
  { pattern: /^\/proyectos\/[^/]+\/observaciones$/, title: "Observaciones" },
  { pattern: /^\/proyectos\/[^/]+\/consultas$/, title: "Consultas del proyecto" },
  { pattern: /^\/proyectos\/[^/]+\/archivo$/, title: "Archivo" },
  { pattern: /^\/proyectos\/[^/]+$/, title: "Proyecto" },
];

export function titleFor(pathname: string, navItems: readonly VisibleNavItem[]): string {
  const subtree = PROJECT_SUBTREE_TITLES.find((t) => t.pattern.test(pathname));
  if (subtree) return subtree.title;
  const match = navItems.find((n) => {
    const base = navItemBasePath(n);
    return pathname === base || (base !== "/" && pathname.startsWith(`${base}/`));
  });
  if (match) return match.label;
  if (pathname.startsWith("/admin")) return "Panel interno";
  return pathname;
}

export function RouteAnnouncer() {
  const location = useLocation();
  const navItems = useVisibleNavItems();
  const [announcement, setAnnouncement] = useState("");
  const mounted = useRef(false);
  // react-router marca con key === "default" la entrada inicial del
  // history stack (login, F5, pestaña nueva); cualquier navegación
  // posterior crea una entrada con key generada.
  const isInitialEntry = location.key === "default";

  useEffect(() => {
    // No anunciar el montaje inicial cuando es la primera entrada del
    // stack — el primer render ya es lo que el usuario esperaba al
    // autenticarse o al recargar; el anuncio es para navegaciones.
    //
    // Pero si el Shell se monta sobre una entrada que NO es la inicial,
    // este montaje es una vuelta al Shell desde una ruta que vive fuera
    // de él (/reportes/:id no tiene sidebar ni announcer, y sus migas
    // navegan de vuelta con el cliente): ahí sí hubo navegación y el
    // anuncio corresponde. Se decide con location.key en lugar de una
    // marca persistida: no hay nada que limpiar en logout, una recarga
    // vuelve a ser silenciosa, y funciona igual en la pestaña nueva
    // donde sessionStorage arranca vacío.
    if (!mounted.current) {
      mounted.current = true;
      if (isInitialEntry) return;
    }
    // Vaciar antes de re-escribir: un mismo texto reescrito sin pasar
    // por vacío puede no ser anunciado por algunos lectores de pantalla.
    setAnnouncement("");
    const id = window.setTimeout(() => {
      setAnnouncement(`Página cargada: ${titleFor(location.pathname, navItems)}.`);
    }, 50);
    return () => window.clearTimeout(id);
    // navItems is deliberately not a dependency: the announcement is for
    // a navigation, not for the recipe or a badge count resolving later
    // on the same page. The list read at announce time is whatever the
    // 50 ms window sees, which on a client-side navigation is the
    // already-resolved one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isInitialEntry]);

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
}
